import { apiClient } from './apiClient';

export type SupportChannelEmailSettings = {
  imapHost?: string | null;
  imapPort: number;
  imapUsername?: string | null;
  imapUseSsl: boolean;
  hasImapPassword: boolean;
  smtpHost?: string | null;
  smtpPort: number;
  smtpUsername?: string | null;
  smtpUseTls: boolean;
  smtpFromAddress?: string | null;
  smtpFromName?: string | null;
  signatureHtml?: string | null;
  hasSmtpPassword: boolean;
};

export type SupportChannelEmailSettingsUpsert = {
  imapHost?: string;
  imapPort: number;
  imapUsername?: string;
  imapPassword?: string;
  imapUseSsl: boolean;
  smtpHost?: string;
  smtpPort: number;
  smtpUsername?: string;
  smtpPassword?: string;
  smtpUseTls: boolean;
  smtpFromAddress?: string;
  smtpFromName?: string;
  signatureHtml?: string;
};

export type SupportChannelApiSettings = {
  mode: string;
  method: string;
  url?: string | null;
  outboundUrl?: string | null;
  outboundMethod: string;
  outboundBodyTemplate?: string | null;
  headersJson?: string | null;
  authType: string;
  authUsername?: string | null;
  hasAuthSecret: boolean;
  apiKeyHeaderName?: string | null;
  webhookSecretHint?: string | null;
  hasWebhookSecret: boolean;
  webhookPath?: string | null;
};

export type SupportChannelApiSettingsUpsert = {
  mode: string;
  method: string;
  url?: string;
  outboundUrl?: string;
  outboundMethod: string;
  outboundBodyTemplate?: string;
  headersJson?: string;
  authType: string;
  authUsername?: string;
  authSecret?: string;
  apiKeyHeaderName?: string;
  webhookSecret?: string;
};

export type SupportChannelChatSettings = {
  baseUrl?: string | null;
  accountId: number;
  inboxId?: string | null;
  assistantName?: string | null;
  hasApiAccessToken: boolean;
  hasWebhookSecret: boolean;
  webhookPath?: string | null;
};

export type SupportChannelChatSettingsUpsert = {
  baseUrl?: string;
  accountId: number;
  inboxId?: string;
  assistantName?: string;
  apiAccessToken?: string;
  webhookSecret?: string;
};

export type SupportChannelAssistantSettings = {
  enabled: boolean;
  method: string;
  url?: string | null;
  headersJson?: string | null;
  bodyTemplate?: string | null;
  authType: string;
  authUsername?: string | null;
  hasAuthSecret: boolean;
  apiKeyHeaderName?: string | null;
  authRequestMethod: string;
  authRequestUrl?: string | null;
  authRequestHeadersJson?: string | null;
  authRequestBody?: string | null;
  authRequestAuthType: string;
  tokenJsonPath?: string | null;
  tokenApplyAs: string;
  tokenHeaderName?: string | null;
  responseTextJsonPath?: string | null;
  responseThreadIdJsonPath?: string | null;
  responseHandoffJsonPath?: string | null;
  historyMessageCount: number;
  escalateToHumanOnFailure: boolean;
};

export type SupportChannelAssistantSettingsUpsert = {
  enabled: boolean;
  method: string;
  url?: string;
  headersJson?: string;
  bodyTemplate?: string;
  authType: string;
  authUsername?: string;
  authSecret?: string;
  apiKeyHeaderName?: string;
  authRequestMethod: string;
  authRequestUrl?: string;
  authRequestHeadersJson?: string;
  authRequestBody?: string;
  authRequestAuthType: string;
  tokenJsonPath?: string;
  tokenApplyAs: string;
  tokenHeaderName?: string;
  responseTextJsonPath?: string;
  responseThreadIdJsonPath?: string;
  responseHandoffJsonPath?: string;
  historyMessageCount: number;
  escalateToHumanOnFailure: boolean;
};

export type SupportChannelConfig = {
  id: string;
  channelType: string;
  name: string;
  isEnabled: boolean;
  externalInboxId?: string | null;
  emailSettings?: SupportChannelEmailSettings | null;
  apiSettings?: SupportChannelApiSettings | null;
  chatSettings?: SupportChannelChatSettings | null;
  assistantSettings?: SupportChannelAssistantSettings | null;
  webhookSettings?: SupportChannelWebhookSettings | null;
  attendanceMode?: string;
  attendanceModeLabel?: string;
  defaultCompanyId: string;
  defaultCompanyName?: string | null;
  defaultAssignedTeamId?: string | null;
  defaultAssignedTeamName?: string | null;
  defaultSlaId?: string | null;
  defaultSlaName?: string | null;
  outboundChannelConfigId?: string | null;
  outboundChannelName?: string | null;
  outboundChannelType?: string | null;
  routingRulesJson?: string | null;
  csatEnabled?: boolean;
  npsEnabled?: boolean;
  csatSurveyFormId?: string | null;
  csatSurveyFormName?: string | null;
  npsSurveyFormId?: string | null;
  npsSurveyFormName?: string | null;
  csatSendOnResolved?: boolean;
  csatSendOnClosed?: boolean;
  csatSendOnArchived?: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SupportChannelConfigUpsertPayload = {
  channelType: string;
  name: string;
  isEnabled: boolean;
  externalInboxId?: string;
  emailSettings?: SupportChannelEmailSettingsUpsert;
  apiSettings?: SupportChannelApiSettingsUpsert;
  chatSettings?: SupportChannelChatSettingsUpsert;
  assistantSettings?: SupportChannelAssistantSettingsUpsert;
  webhookSettings?: SupportChannelWebhookSettingsUpsert;
  attendanceMode?: string;
  defaultCompanyId: string;
  defaultAssignedTeamId?: string;
  defaultSlaId?: string;
  outboundChannelConfigId?: string;
  routingRulesJson?: string;
  csatEnabled?: boolean;
  npsEnabled?: boolean;
  csatSurveyFormId?: string;
  npsSurveyFormId?: string;
  csatSendOnResolved?: boolean;
  csatSendOnClosed?: boolean;
  csatSendOnArchived?: boolean;
};

export type SupportChannelAttendanceMode =
  | 'Manual'
  | 'AutoReply'
  | 'Assistant'
  | 'ExternalBot';

export const ATTENDANCE_MODE_LABELS: Record<SupportChannelAttendanceMode, string> = {
  Manual: 'Manual',
  AutoReply: 'Auto-reply',
  Assistant: 'Assistente',
  ExternalBot: 'Bot externo',
};

export const emptyEmailSettings = (): SupportChannelEmailSettingsUpsert => ({
  imapHost: '',
  imapPort: 993,
  imapUsername: '',
  imapPassword: '',
  imapUseSsl: true,
  smtpHost: '',
  smtpPort: 587,
  smtpUsername: '',
  smtpPassword: '',
  smtpUseTls: true,
  smtpFromAddress: '',
  smtpFromName: '',
  signatureHtml: '',
});

export const emptyApiSettings = (): SupportChannelApiSettingsUpsert => ({
  mode: 'Webhook',
  method: 'POST',
  url: '',
  outboundUrl: '',
  outboundMethod: 'POST',
  outboundBodyTemplate:
    '{\n  "ticketId": "{{ticketId}}",\n  "ticketCode": "{{ticketCode}}",\n  "externalConversationId": "{{externalConversationId}}",\n  "body": "{{body}}",\n  "to": "{{to}}"\n}',
  headersJson: '{\n  "Content-Type": "application/json"\n}',
  authType: 'None',
  authUsername: '',
  authSecret: '',
  apiKeyHeaderName: 'X-Api-Key',
  webhookSecret: '',
});

export const emptyChatSettings = (): SupportChannelChatSettingsUpsert => ({
  baseUrl: '',
  accountId: 0,
  inboxId: '',
  assistantName: '',
  apiAccessToken: '',
  webhookSecret: '',
});

export type SupportChannelWebhookStatusMapEntry = {
  goStatus: string;
  externalStatus: string;
};

export type SupportChannelWebhookSettings = {
  enabled: boolean;
  method: string;
  url?: string | null;
  headersJson?: string | null;
  bodyTemplate?: string | null;
  authType: string;
  authUsername?: string | null;
  hasAuthSecret: boolean;
  apiKeyHeaderName?: string | null;
  statusMap: SupportChannelWebhookStatusMapEntry[];
};

export type SupportChannelWebhookSettingsUpsert = {
  enabled: boolean;
  method: string;
  url?: string;
  headersJson?: string;
  bodyTemplate?: string;
  authType: string;
  authUsername?: string;
  authSecret?: string;
  apiKeyHeaderName?: string;
  statusMap: SupportChannelWebhookStatusMapEntry[];
};

export const WEBHOOK_BODY_TEMPLATE = `{
  "event": "{{event}}",
  "actor": "{{actor}}",
  "ticketId": "{{ticketId}}",
  "ticketCode": "{{ticketCode}}",
  "externalId": "{{externalId}}",
  "message": "{{message}}",
  "status": "{{status}}",
  "previousStatus": "{{previousStatus}}"
}`;

export const emptyWebhookSettings = (): SupportChannelWebhookSettingsUpsert => ({
  enabled: false,
  method: 'POST',
  url: '',
  headersJson: '{\n  "Content-Type": "application/json"\n}',
  bodyTemplate: WEBHOOK_BODY_TEMPLATE,
  authType: 'None',
  authUsername: '',
  authSecret: '',
  apiKeyHeaderName: 'X-Api-Key',
  statusMap: [],
});

export const emptyAssistantSettings = (): SupportChannelAssistantSettingsUpsert => ({
  enabled: false,
  method: 'POST',
  url: '',
  headersJson: '{\n  "Content-Type": "application/json"\n}',
  bodyTemplate:
    '{\n  "agentId": "",\n  "threadId": "{{threadId}}",\n  "message": "{{message}}",\n  "history": "{{history}}"\n}',
  authType: 'None',
  authUsername: '',
  authSecret: '',
  apiKeyHeaderName: 'X-Api-Key',
  authRequestMethod: 'POST',
  authRequestUrl: '',
  authRequestHeadersJson: '{\n  "Content-Type": "application/json"\n}',
  authRequestBody: '',
  authRequestAuthType: 'None',
  tokenJsonPath: 'access_token',
  tokenApplyAs: 'Bearer',
  tokenHeaderName: 'Authorization',
  responseTextJsonPath: 'textResponse',
  responseThreadIdJsonPath: 'threadId',
  responseHandoffJsonPath: 'handoff',
  historyMessageCount: 0,
  escalateToHumanOnFailure: true,
});

const ROUTING_EXAMPLE = `{
  "ignoreAddresses": ["@mailer-daemon", "spam@"],
  "doNotReplyAddresses": ["noreply@", "no-reply@", "btalk-suporte@"],
  "alertGroupWindowHours": 24,
  "alertGroupAddresses": ["btalk-suporte@"],
  "ignoreContent": [
    { "keywords": ["[spam]"], "matchIn": "Subject" },
    { "keywords": ["unsubscribe"], "matchIn": "Body" },
    { "keywords": ["out of office"], "matchIn": "Both" }
  ],
  "keywordCatalogRules": [
    { "keywords": ["reset", "senha"], "catalogItemId": "13131313-1313-1313-1313-131313131301", "matchIn": "Subject" },
    { "keywords": ["fatura", "boleto"], "catalogItemId": "13131313-1313-1313-1313-131313131302", "matchIn": "Both" }
  ],
  "businessHoursCalendarId": "00000000-0000-0000-0000-000000000000",
  "afterHoursAction": "Defer",
  "afterHoursAutoReplyMessage": "Recebemos sua mensagem fora do horário comercial. Retornaremos em breve."
}`;

export { ROUTING_EXAMPLE };

export async function listSupportChannels(): Promise<SupportChannelConfig[]> {
  const response = await apiClient.get<SupportChannelConfig[]>('/v1/customer-success/channels');
  return response.data;
}

export async function createSupportChannel(
  payload: SupportChannelConfigUpsertPayload,
): Promise<SupportChannelConfig> {
  const response = await apiClient.post<SupportChannelConfig>('/v1/customer-success/channels', payload);
  return response.data;
}

export async function updateSupportChannel(
  id: string,
  payload: SupportChannelConfigUpsertPayload,
): Promise<SupportChannelConfig> {
  const response = await apiClient.put<SupportChannelConfig>(`/v1/customer-success/channels/${id}`, payload);
  return response.data;
}

export async function deleteSupportChannel(id: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/channels/${id}`);
}

export async function duplicateSupportChannel(id: string): Promise<SupportChannelConfig> {
  const response = await apiClient.post<SupportChannelConfig>(
    `/v1/customer-success/channels/${id}/duplicate`,
  );
  return response.data;
}

export type SupportChannelSmtpTestResult = {
  sentTo: string;
  fromAddress: string;
};

export type SupportChannelAssistantTestResult = {
  succeeded: boolean;
  replyPreview?: string | null;
  threadId?: string | null;
  handoff: boolean;
};

export async function testSupportChannelSmtp(
  id: string,
  to?: string,
): Promise<SupportChannelSmtpTestResult> {
  const response = await apiClient.post<SupportChannelSmtpTestResult>(
    `/v1/customer-success/channels/${id}/test-smtp`,
    { to: to?.trim() || undefined },
  );
  return response.data;
}

export async function testSupportChannelAssistant(
  id: string,
): Promise<SupportChannelAssistantTestResult> {
  const response = await apiClient.post<SupportChannelAssistantTestResult>(
    `/v1/customer-success/channels/${id}/test-assistant`,
  );
  return response.data;
}
